import { Button, Card, Col, Flex, Row } from 'antd';
import { IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { PermissionKey } from '@evimo/types/enums/permission-key';
import { ErrorBox } from '~/app/components/common/Error/ErrorBox';
import { Loader } from '~/app/components/common/Loader/Loader';
import type { RolesPageViewProps } from './types';
import { AuthorizationPageHeader } from './components/AuthorizationPageHeader/AuthorizationPageHeader';
import { CreateRoleModal } from './components/CreateRoleModal/CreateRoleModal';
import { DeleteRoleModal } from './components/DeleteRoleModal/DeleteRoleModal';
import { RoleCardGrid } from './components/RoleCardGrid/RoleCardGrid';
import { SystemRoleCardGrid } from './components/SystemRoleCardGrid/SystemRoleCardGrid';

type RolesPageViewFullProps = RolesPageViewProps & {
  onCreateRole: (payload: {
    name: string;
    cloneFromRoleId?: string;
    permissionKeys?: PermissionKey[];
  }) => Promise<void>;
  onDeleteRole: (roleId: string, fallbackRoleId?: string) => Promise<void>;
  isCreateSubmitting?: boolean;
  isDeleteSubmitting?: boolean;
};

export function RolesPageView({
  isSuperAdmin,
  organizationId,
  systemRoles,
  customRoles,
  cloneSourceRoles,
  fallbackRoles,
  isLoading,
  error,
  canCreateRole,
  canDeleteRole,
  userRoleName,
  isCreateModalOpen,
  onCreateModalOpenChange,
  deletingRole,
  onDeletingRoleChange,
  onViewRole,
  onCreateRole,
  onDeleteRole,
  isCreateSubmitting,
  isDeleteSubmitting,
}: RolesPageViewFullProps) {
  const { t } = useTranslation('authorization');

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <ErrorBox title={t('page.errorTitle')} description={error.message} />
    );
  }

  return (
    <>
      <Flex className="app-page authorization-page" vertical>
        <Row gutter={[0, 24]}>
          <Col span={24}>
            <AuthorizationPageHeader
              title={t('page.title')}
              subtitle={
                isSuperAdmin ? t('page.subtitleSuperAdmin') : t('page.subtitle')
              }
              actions={
                canCreateRole && organizationId ? (
                  <Button
                    type="primary"
                    size="large"
                    icon={<IconPlus size={16} />}
                    onClick={() => onCreateModalOpenChange(true)}
                  >
                    {t('page.createRole')}
                  </Button>
                ) : undefined
              }
            />
          </Col>

          <Col span={24}>
            <Card className="authorization-section-card authorization-system-roles-section">
              <Flex
                vertical
                gap={4}
                className="authorization-section-card-header authorization-section-card-header--system"
              >
                <span className="authorization-section-card-title">
                  {t('sections.systemRoles')}
                </span>
                <span className="authorization-section-card-subtitle">
                  {t('sections.systemRolesSubtitle')}
                </span>
              </Flex>
              <SystemRoleCardGrid
                roles={systemRoles}
                userRoleName={userRoleName}
                showUsersCount={!isSuperAdmin}
                onView={onViewRole}
              />
            </Card>
          </Col>

          {!isSuperAdmin && (
            <Col span={24}>
              <Card className="authorization-section-card authorization-custom-roles-section">
                <Flex
                  vertical
                  gap={4}
                  className="authorization-section-card-header authorization-section-card-header--custom"
                >
                  <span className="authorization-section-card-title">
                    {t('sections.customRoles')}
                  </span>
                  <span className="authorization-section-card-subtitle">
                    {t('sections.customRolesSubtitle')}
                  </span>
                </Flex>
                <RoleCardGrid
                  roles={customRoles}
                  canDeleteRole={canDeleteRole}
                  onView={onViewRole}
                  onDelete={onDeletingRoleChange}
                />
              </Card>
            </Col>
          )}
        </Row>
      </Flex>

      {!isSuperAdmin && organizationId && (
        <>
          <CreateRoleModal
            open={isCreateModalOpen}
            roles={cloneSourceRoles}
            loading={isCreateSubmitting}
            onOpenChange={onCreateModalOpenChange}
            onSubmit={onCreateRole}
          />

          <DeleteRoleModal
            open={Boolean(deletingRole)}
            organizationId={organizationId}
            role={deletingRole}
            roles={fallbackRoles}
            loading={isDeleteSubmitting}
            onOpenChange={(open) => {
              if (!open) onDeletingRoleChange(null);
            }}
            onConfirm={onDeleteRole}
          />
        </>
      )}
    </>
  );
}
